import React, { useState } from 'react';
import { defaults } from 'react-chartjs-2';

import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import { isAdmin } from '@/utils/Util';
import {
	useGetDashboardCardListQuery,
	useDeleteDashboardCardMutation,
	useGetRoleWiseDashboardCardsQuery,
	useTrackDashboardCardUsageMutation
} from '@/features/dashboard/api/DashboardApi';
import CommonImage from '@/components/CommonImage';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';

import AddAndEditDashboardModal from './AddAndEditDashboardModal';

defaults.font.family = 'Open sans';

const getDashboardTagText = (tag) => {
	if (typeof tag === 'string' || typeof tag === 'number') {
		return String(tag).trim();
	}

	if (tag && typeof tag === 'object') {
		return String(tag.label || tag.name || tag.title || tag.value || tag.id || '').trim();
	}

	return '';
};

const getDashboardTags = (dashboard = {}) => {
	const source = dashboard?.tags ?? dashboard?.tagIds ?? [];
	const list = Array.isArray(source) ? source : [source];

	return Array.from(new Set(list.map(getDashboardTagText).filter(Boolean)));
};

const getDashboardCardItems = (items = []) => {
	return Array.isArray(items) ? items : items?.data || [];
};

const AdminDashBoard = () => {
	const [showAddAndEditDashboardModal, setShowAddAndEditDashboardModal] = useState(false);
	const [selectedDashboardCard, setSelectedDashboardCard] = useState(null);
	const [dashboardCardToDelete, setDashboardCardToDelete] = useState(null);

	const { data: dashboardCardList = [], refetch: refetchDashboardCardList } =
		useGetDashboardCardListQuery();

	const [deleteDashboardCard, { isLoading: isDeleteDashboardCardLoading }] =
		useDeleteDashboardCardMutation();

	const [trackDashboardCardUsage] = useTrackDashboardCardUsageMutation();

	const { data: roleWiseDashboardCards = [] } = useGetRoleWiseDashboardCardsQuery();
	const dashboardCards = getDashboardCardItems(isAdmin() ? dashboardCardList : roleWiseDashboardCards);
	const hasDashboardCards = dashboardCards.length > 0;

	const onDashboardCardClick = (dashboard) => {
		if (dashboard?.id) {
			trackDashboardCardUsage({ cardId: dashboard.id });
		}

		if (dashboard?.detailsUrl) {
			window.open(dashboard.detailsUrl, '_blank', 'noopener,noreferrer');
		}
	};

	const openAddModal = () => {
		setSelectedDashboardCard(null);
		setShowAddAndEditDashboardModal(true);
	};

	const openEditModal = (dashboardCard) => {
		setSelectedDashboardCard(dashboardCard || null);
		setShowAddAndEditDashboardModal(true);
	};

	const openDeleteModal = (dashboardCard) => {
		setDashboardCardToDelete(dashboardCard || null);
	};

	const closeDeleteModal = () => {
		setDashboardCardToDelete(null);
	};

	const onDeleteDashboardCard = async () => {
		const id = dashboardCardToDelete?.id;
		if (!id) {
			return;
		}

		try {
			await deleteDashboardCard({ id }, { skip: !id }).unwrap();
			toastSuccess('Dashboard card deleted successfully');
			refetchDashboardCardList();
			closeDeleteModal();
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong! Please try again.');
		}
	};

	return (
		<LoaderComponent loading={isDeleteDashboardCardLoading}>
			<div className="top-header body-bg-gray">
				<div className="page-title-area  body-bg-gray pb-2 d-flex align-items-center justify-content-between">
					<h1 className="semibold-16">Dashboard</h1>
					{isAdmin() && (
						<button className="button size32" onClick={openAddModal}>
							Add Dashboard
						</button>
					)}
				</div>
			</div>
			<div className="full-dashboard">
				<div className="main-dashboard-cards">
					{hasDashboardCards ? (
						<div className="dashboards-cards d-grid grid-columns-3 gap-4 mb-4">
							{dashboardCards.map((dashboard) => (
								<div
									key={dashboard.id || dashboard.title}
									className="single-dashboard-card cursor-pointer bg-white position-relative d-flex flex-column justify-content-between"
									onClick={() => onDashboardCardClick(dashboard)}
								>
									<div className="dashboard-card-header">
										{isAdmin() && (
											<div
												className="dashboard-card-actions position-absolute top-2 right-2 d-flex gap-2"
												onClick={(event) => event.stopPropagation()}
											>
												<span
													className="action-btn cursor-pointer bg-white d-flex justify-content-center align-items-center"
													onClick={(event) => {
														event.stopPropagation();
														openEditModal(dashboard);
													}}
												>
													<CommonImage
														src="/icons/edit-tool.svg"
														width={20}
														height={20}
													/>
												</span>
												<span
													className="action-btn cursor-pointer bg-white d-flex justify-content-center align-items-center"
													onClick={(event) => {
														event.stopPropagation();
														openDeleteModal(dashboard);
													}}
												>
													<CommonImage
														src="/icons/delete-dark-with-bg.svg"
														width={20}
														height={20}
													/>
												</span>
											</div>
										)}
									</div>
									<div>
										<h3 className="dashboard-card-title weight-600">{dashboard.title}</h3>
										<p className="dashboard-card-description regular-14 gray_dark_01">
											{dashboard.description}
										</p>
									</div>

									<div className="dashboard-card-footer mt-2 d-flex justify-content-between align-items-center">
										<span
											className="view-dashboard-link gray_dark_01 cursor-pointer"
											role="button"
											tabIndex={0}
											onClick={(event) => {
												event.stopPropagation();
												onDashboardCardClick(dashboard);
											}}
										>
											View dashboard{' '}
											<span className="ms-1">
												<CommonImage
													style={{ transform: 'rotate(180deg)' }}
													src="/icons/arrow-back.svg"
													width={16}
													height={16}
												/>
											</span>
										</span>
										<div className="d-flex align-items-center flex-wrap justify-content-end gap-2">
											{getDashboardTags(dashboard).map((tag) => (
												<span
													key={`${dashboard.id || dashboard.title}-${tag}`}
													className="status gray-1"
												>
													{tag}
												</span>
											))}
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="bg-white border radius-1 p-4 mb-4 text-center">
							<p className="regular-14 gray_dark_02 mb-0">No card found</p>
						</div>
					)}
				</div>

				{showAddAndEditDashboardModal && (
					<AddAndEditDashboardModal
						onClose={() => setShowAddAndEditDashboardModal(false)}
						selectedCard={selectedDashboardCard}
						onSuccess={refetchDashboardCardList}
					/>
				)}

				{dashboardCardToDelete && (
					<DeleteConfirmationModal
						onClose={closeDeleteModal}
						onConfirm={onDeleteDashboardCard}
						title="Delete Dashboard Card"
						description="Are you sure you want to delete this dashboard card?"
					/>
				)}
			</div>
		</LoaderComponent>
	);
};

export default AdminDashBoard;
